import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { freeAgencyAPI } from '../../api/api';
import { useAuth } from '../../context/AuthContext';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function formatCountdown(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Abgelaufen';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDateTime(dt) {
  if (!dt) return '–';
  return new Date(dt).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const FreeAgencyManagement = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fa, setFa] = useState(null);
  const [picks, setPicks] = useState([]);
  const [teams, setTeams] = useState([]); // FA participating teams
  const [allTeams, setAllTeams] = useState([]);
  const [season, setSeason] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [selectedPickId, setSelectedPickId] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [view, setView] = useState('setup'); // 'setup' | 'draft' | 'results'

  // Setup form state
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    startTime: '11:00',
    dailyStartTime: '11:00',
    dailyEndTime: '22:00',
    totalRounds: 1,
    pickTimeMinutes: 90,
    orderType: 'custom',
    participatingTeamIds: []
  });

  // Drag-and-drop order
  const [pickOrder, setPickOrder] = useState([]); // array of team objects
  const [dragIndex, setDragIndex] = useState(null);

  // Countdown ticker
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh during active FA
  useEffect(() => {
    if (fa?.status === 'in_progress') {
      const t = setInterval(fetchFaState, 10000);
      return () => clearInterval(t);
    }
  }, [fa?.status]);

  // ── Initial load ──────────────────────────
  useEffect(() => {
    fetchSeason();
    fetchAllTeams();
    fetchFaState();
  }, [seasonId]);

  const fetchSeason = async () => {
    try {
      const res = await api.get(`/seasons/${seasonId}`);
      setSeason(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAllTeams = async () => {
    try {
      const res = await api.get('/teams');
      setAllTeams(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFaState = async () => {
    try {
      const res = await api.get(`/free-agency/season/${seasonId}`);
      const { freeAgency, picks: p, teams: t } = res.data;
      setFa(freeAgency);
      setPicks(p);
      setTeams(t);
      if (freeAgency.status === 'not_started') {
        setView('setup');
        setPickOrder(t);
      } else if (freeAgency.status === 'completed') {
        setView('results');
      } else {
        setView('draft');
      }
      setLoading(false);
    } catch (e) {
      if (e.response?.status === 404) {
        setFa(null);
        setLoading(false);
      } else {
        console.error(e);
      }
    }
  };

  const fetchAvailablePlayers = useCallback(async () => {
    if (!fa) return;
    try {
      const params = new URLSearchParams();
      if (playerSearch) params.set('search', playerSearch);
      if (positionFilter) params.set('position', positionFilter);
      const res = await api.get(`/free-agency/${fa.id}/available-players?${params}`);
      setAvailablePlayers(res.data.players);
    } catch (e) {
      console.error(e);
    }
  }, [fa, playerSearch, positionFilter]);

  useEffect(() => {
    if (view === 'draft') fetchAvailablePlayers();
  }, [view, playerSearch, positionFilter, fetchAvailablePlayers]);

  // ── Create FA ─────────────────────────────
  const handleCreate = async () => {
    if (!form.name || !form.startDate || form.participatingTeamIds.length === 0) {
      alert('Bitte Name, Startdatum und mindestens ein Team angeben.');
      return;
    }
    setSaving(true);
    try {
      const startDate = new Date(`${form.startDate}T${form.startTime}:00`);
      await api.post('/free-agency', {
        seasonId: parseInt(seasonId),
        name: form.name,
        startDate,
        dailyStartTime: form.dailyStartTime,
        dailyEndTime: form.dailyEndTime,
        totalRounds: parseInt(form.totalRounds),
        pickTimeMinutes: parseInt(form.pickTimeMinutes),
        orderType: form.orderType,
        participatingTeamIds: form.participatingTeamIds
      });
      await fetchFaState();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  // ── Save order after drag-drop ────────────
  const handleSaveOrder = async () => {
    if (!fa) return;
    setSaving(true);
    try {
      await api.put(`/free-agency/${fa.id}/order`, {
        participatingTeamIds: pickOrder.map(t => t.id)
      });
      await fetchFaState();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Speichern der Reihenfolge');
    } finally {
      setSaving(false);
    }
  };

  // ── Start FA ──────────────────────────────
  const handleStart = async () => {
    if (!fa) return;
    if (!window.confirm('Free Agency wirklich starten?')) return;
    setSaving(true);
    try {
      await api.post(`/free-agency/${fa.id}/start`);
      await fetchFaState();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Starten');
    } finally {
      setSaving(false);
    }
  };

  // ── Make a pick (admin) ───────────────────
  const handleMakePick = async () => {
    if (!selectedPickId || !selectedPlayerId) {
      alert('Bitte Pick-Slot und Spieler auswählen.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/free-agency/${fa.id}/picks/${selectedPickId}`, {
        playerId: parseInt(selectedPlayerId)
      });
      setSelectedPickId(null);
      setSelectedPlayerId('');
      await fetchFaState();
      await fetchAvailablePlayers();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Pick');
    } finally {
      setSaving(false);
    }
  };

  // ── Skip a pick (admin) ───────────────────
  const handleSkip = async (pickId) => {
    if (!pickId) return;
    if (!window.confirm('Diesen Pick überspringen? Das nächste Team kommt an die Reihe. Der Pick bleibt nachholbar.')) return;
    setSaving(true);
    try {
      await api.post(`/free-agency/${fa.id}/picks/${pickId}/skip`);
      setSelectedPickId(null);
      await fetchFaState();
      await fetchAvailablePlayers();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Überspringen');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete FA ─────────────────────────────
  const handleDelete = async () => {
    if (!fa) return;
    if (!window.confirm('Free Agency wirklich löschen?')) return;
    setSaving(true);
    try {
      await api.delete(`/free-agency/${fa.id}`);
      setFa(null);
      setPicks([]);
      setTeams([]);
      setPickOrder([]);
      setView('setup');
      setForm({
        name: '', startDate: '', startTime: '11:00',
        dailyStartTime: '11:00', dailyEndTime: '22:00',
        totalRounds: 1, pickTimeMinutes: 90, orderType: 'custom',
        participatingTeamIds: []
      });
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Löschen');
    } finally {
      setSaving(false);
    }
  };

  // ── FA manuell beenden ────────────────────
  // Picks per Verzicht oder Timeout behalten playerId NULL, die Auto-Completion
  // im Server greift dann nie. Beenden ist deshalb eine Admin-Aktion.
  const handleComplete = async () => {
    if (!fa) return;
    const openCount = picks.filter(p => !p.playerId).length;
    const openHint = openCount > 0
      ? `\n\n${openCount} Pick(s) ohne Spieler bleiben als Verzicht dokumentiert.`
      : '';
    if (!window.confirm(
      'Free Agency endgültig beenden?\n\nDanach sind keine Picks mehr möglich und die Free Agency kann nicht mehr zurückgesetzt werden.'
      + openHint
    )) return;
    setSaving(true);
    try {
      await freeAgencyAPI.complete(fa.id);
      await fetchFaState();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Beenden');
    } finally {
      setSaving(false);
    }
  };

  // ── Reset FA ──────────────────────────────
  const handleReset = async () => {
    if (!fa) return;
    if (!window.confirm('Free Agency zurücksetzen? Status wird auf "Nicht gestartet" zurückgesetzt. Nur möglich wenn noch kein Pick gemacht wurde.')) return;
    setSaving(true);
    try {
      await api.post(`/free-agency/${fa.id}/reset`);
      await fetchFaState();
    } catch (e) {
      alert(e.response?.data?.message || 'Fehler beim Zurücksetzen');
    } finally {
      setSaving(false);
    }
  };

  // ── Drag-drop handlers ────────────────────
  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    const newOrder = [...pickOrder];
    const [moved] = newOrder.splice(dragIndex, 1);
    newOrder.splice(idx, 0, moved);
    setPickOrder(newOrder);
    setDragIndex(idx);
  };
  const handleDragEnd = () => setDragIndex(null);

  // ── Derived pick data ─────────────────────
  const activePick = picks.find(p => p.status === 'active');
  const openPicks = picks.filter(p => p.status === 'active' || p.status === 'expired');
  const completedPicks = picks.filter(p => p.status === 'completed');
  const noPicksMade = picks.every(p => !p.playerId);
  const startDatePassed = fa && new Date(fa.startDate) <= new Date();
  const isArmed = fa && fa.status === 'in_progress' && !startDatePassed && noPicksMade;

  // ──────────────────────────────────────────
  // Render: loading
  // ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Lade Free Agency…</div>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // Render: no FA yet – creation form
  // ──────────────────────────────────────────
  if (!fa) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <button onClick={() => navigate(`/admin/seasons`)} className="text-blue-400 hover:underline mb-6 block">
          ← Zurück zu Seasons
        </button>
        <h1 className="text-3xl font-bold mb-2">Free Agency erstellen</h1>
        <p className="text-gray-400 mb-8">Season: {season?.name}</p>

        <div className="bg-gray-800 rounded-xl p-6 max-w-2xl space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
            <input
              type="text"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              placeholder="z.B. 2026 Free Agency"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
          </div>

          {/* Start date / time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Startdatum</label>
              <input
                type="date"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Startzeit</label>
              <input
                type="time"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                value={form.startTime}
                onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
              />
            </div>
          </div>

          {/* Daily window */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tägliches Fenster: Beginn</label>
              <input
                type="time"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                value={form.dailyStartTime}
                onChange={e => setForm(f => ({ ...f, dailyStartTime: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tägliches Fenster: Ende</label>
              <input
                type="time"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                value={form.dailyEndTime}
                onChange={e => setForm(f => ({ ...f, dailyEndTime: e.target.value }))}
              />
            </div>
          </div>

          {/* Rounds & pick time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Anzahl Runden</label>
              <input
                type="number"
                min={1}
                max={10}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                value={form.totalRounds}
                onChange={e => setForm(f => ({ ...f, totalRounds: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Pick-Zeit (Minuten)</label>
              <input
                type="number"
                min={10}
                max={1440}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                value={form.pickTimeMinutes}
                onChange={e => setForm(f => ({ ...f, pickTimeMinutes: e.target.value }))}
              />
            </div>
          </div>

          {/* Order type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Reihenfolge</label>
            <select
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              value={form.orderType}
              onChange={e => setForm(f => ({ ...f, orderType: e.target.value }))}
            >
              <option value="custom">Manuell (Drag & Drop)</option>
              <option value="fantasy_snake">Umgekehrt vom Fantasy Draft (Snake)</option>
            </select>
            {form.orderType === 'fantasy_snake' && (
              <p className="text-xs text-yellow-400 mt-1">
                Letzter im Draft-Runde-1 pickt zuerst. Jede weitere Runde wird die Reihenfolge umgekehrt (Snake).
              </p>
            )}
          </div>

          {/* Participating teams */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Teilnehmende Teams</label>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {allTeams.map(t => (
                <label key={t.id} className="flex items-center gap-2 bg-gray-700 rounded px-3 py-2 cursor-pointer hover:bg-gray-600">
                  <input
                    type="checkbox"
                    className="accent-blue-500"
                    checked={form.participatingTeamIds.includes(t.id)}
                    onChange={e => {
                      setForm(f => ({
                        ...f,
                        participatingTeamIds: e.target.checked
                          ? [...f.participatingTeamIds, t.id]
                          : f.participatingTeamIds.filter(id => id !== t.id)
                      }));
                    }}
                  />
                  <span className="text-sm">{t.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleCreate}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-2 rounded-lg font-semibold"
            >
              {saving ? 'Erstelle…' : 'Free Agency erstellen'}
            </button>
            <button
              onClick={() => navigate('/admin/seasons')}
              className="bg-gray-600 hover:bg-gray-50 dark:bg-gray-8000 px-6 py-2 rounded-lg"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // Render: FA exists – Setup / Draft / Results views
  // ──────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => navigate('/admin/seasons')} className="text-blue-400 hover:underline mb-2 block text-sm">
            ← Zurück zu Seasons
          </button>
          <h1 className="text-3xl font-bold">{fa.name}</h1>
          <p className="text-gray-400 text-sm mt-1">
            Season: {season?.name} · Täglich {fa.dailyStartTime}–{fa.dailyEndTime} · {fa.pickTimeMinutes} Min/Pick · {fa.totalRounds} Runde(n)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
            fa.status === 'not_started' ? 'bg-gray-600 text-gray-200' :
            isArmed ? 'bg-yellow-600 text-white' :
            fa.status === 'in_progress' ? 'bg-green-600 text-white' :
            'bg-blue-600 text-white'
          }`}>
            {fa.status === 'not_started' ? 'Nicht gestartet' :
             isArmed ? `⏰ Startet ${formatDateTime(fa.startDate)}` :
             fa.status === 'in_progress' ? 'Läuft' : 'Abgeschlossen'}
          </span>
          {fa.status === 'not_started' && isAdmin && (
            <>
              <button
                onClick={handleStart}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold"
              >
                Starten / Planen
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="bg-red-700 hover:bg-red-600 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold"
              >
                Löschen
              </button>
            </>
          )}
          {fa.status === 'in_progress' && isAdmin && (
            <button
              onClick={handleComplete}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold"
              title="Free Agency abschließen – offene Picks gelten als Verzicht, danach kein Zurücksetzen mehr"
            >
              ✓ Free Agency beenden
            </button>
          )}
          {(isArmed || (fa.status === 'in_progress' && noPicksMade)) && isAdmin && (
            <button
              onClick={handleReset}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-500 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold"
              title="Zurücksetzen auf 'Nicht gestartet' (nur möglich wenn noch keine Picks gemacht wurden)"
            >
              ↩ Zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      {fa.status !== 'not_started' && (
        <div className="flex gap-2 mb-6">
          {[['draft', 'Draft Board'], ['results', 'Ergebnisse']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${view === v ? 'bg-blue-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ─── SETUP VIEW ─── */}
      {fa.status === 'not_started' && (
        <div className="space-y-6">
          {/* Info card */}
          <div className="bg-gray-800 rounded-xl p-5">
            <h2 className="text-lg font-bold mb-3">Einstellungen</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-400">Start:</span> {formatDateTime(fa.startDate)}</div>
              <div><span className="text-gray-400">Fenster:</span> {fa.dailyStartTime} – {fa.dailyEndTime}</div>
              <div><span className="text-gray-400">Runden:</span> {fa.totalRounds}</div>
              <div><span className="text-gray-400">Zeit/Pick:</span> {fa.pickTimeMinutes} Min</div>
              <div><span className="text-gray-400">Reihenfolge:</span> {fa.orderType === 'custom' ? 'Manuell' : 'Fantasy Snake'}</div>
            </div>
          </div>

          {/* Draft order editor (custom only) */}
          {fa.orderType === 'custom' && (
            <div className="bg-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">Pick-Reihenfolge (Runde 1)</h2>
                <button
                  onClick={handleSaveOrder}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold"
                >
                  {saving ? 'Speichern…' : 'Reihenfolge speichern'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">Drag & Drop um die Reihenfolge anzupassen. Snake-Logik: Runde 2 wird umgekehrt, Runde 3 wieder wie Runde 1, usw.</p>
              <div className="space-y-2">
                {pickOrder.map((team, idx) => (
                  <div
                    key={team.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={e => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-3 cursor-grab select-none ${dragIndex === idx ? 'opacity-50' : ''}`}
                  >
                    <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                    <span className="text-2xl">⠿</span>
                    <div>
                      <div className="font-semibold">{team.name}</div>
                      {team.owner && (
                        <div className="text-xs text-gray-400">GM: {team.owner.firstName || team.owner.username}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fantasy snake info */}
          {fa.orderType === 'fantasy_snake' && (
            <div className="bg-gray-800 rounded-xl p-5">
              <h2 className="text-lg font-bold mb-3">Pick-Reihenfolge (Fantasy Snake)</h2>
              <p className="text-sm text-yellow-300 mb-4">
                Reihenfolge wird automatisch aus dem Fantasy Draft der Season berechnet (umgekehrt, Snake-Logik).
              </p>
              <div className="space-y-2">
                {teams.map((team, idx) => (
                  <div key={team.id} className="flex items-center gap-3 bg-gray-700 rounded-lg px-4 py-3">
                    <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                    <div>
                      <div className="font-semibold">{team.name}</div>
                      {team.owner && (
                        <div className="text-xs text-gray-400">GM: {team.owner.firstName || team.owner.username}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pick preview */}
          <div className="bg-gray-800 rounded-xl p-5">
            <h2 className="text-lg font-bold mb-4">Pick-Vorschau ({picks.length} Picks)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-600">
                    <th className="text-left pb-2 pr-4">Pick</th>
                    <th className="text-left pb-2 pr-4">Runde</th>
                    <th className="text-left pb-2">Team</th>
                  </tr>
                </thead>
                <tbody>
                  {picks.map(p => (
                    <tr key={p.id} className="border-b border-gray-700 hover:bg-gray-750">
                      <td className="py-2 pr-4 text-gray-400">#{p.overallPick}</td>
                      <td className="py-2 pr-4">R{p.round} P{p.pickNumber}</td>
                      <td className="py-2">{p.team?.name || `Team ${p.teamId}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── ARMED: waiting for scheduled start ─── */}
      {isArmed && (
        <div className="bg-yellow-900 border border-yellow-600 rounded-xl p-6 text-center">
          <div className="text-4xl mb-3">⏰</div>
          <h2 className="text-xl font-bold text-yellow-300 mb-2">Free Agency ist geplant</h2>
          <p className="text-gray-300 mb-1">
            Erster Pick wird automatisch aktiviert am:
          </p>
          <p className="text-2xl font-bold text-yellow-200 mb-4">{formatDateTime(fa.startDate)}</p>
          <p className="text-sm text-gray-400">
            Du musst nichts weiter tun. Sobald dieser Zeitpunkt erreicht wird, wird der erste GM benachrichtigt und der Pick-Counter startet.
          </p>
          {isAdmin && (
            <button
              onClick={handleReset}
              disabled={saving}
              className="mt-5 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 px-5 py-2 rounded-lg font-semibold text-sm"
            >
              ↩ Zurücksetzen &amp; Einstellungen ändern
            </button>
          )}
        </div>
      )}

      {/* ─── DRAFT VIEW ─── */}
      {view === 'draft' && fa.status === 'in_progress' && !isArmed && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Left: current & open picks */}
          <div className="xl:col-span-1 space-y-4">
            {/* Active pick */}
            {activePick && (
              <div className="bg-green-900 border border-green-600 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-green-300 uppercase mb-3">Jetzt am Zug</h3>
                <div className="font-bold text-xl">{activePick.team?.name}</div>
                <div className="text-sm text-gray-300 mt-1">R{activePick.round} · Pick {activePick.pickNumber}</div>
                {activePick.windowExpires && (
                  <div className={`mt-2 text-lg font-mono ${
                    new Date(activePick.windowExpires) - new Date() < 600000 ? 'text-red-400' : 'text-yellow-300'
                  }`}>
                    ⏱ {formatCountdown(activePick.windowExpires)}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">Läuft ab: {formatDateTime(activePick.windowExpires)}</div>
                {isAdmin && (
                  <button
                    onClick={() => handleSkip(activePick.id)}
                    disabled={saving}
                    className="mt-3 w-full bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-gray-200"
                  >
                    Pick überspringen (nachholbar)
                  </button>
                )}
              </div>
            )}

            {/* Expired / pending picks */}
            {openPicks.filter(p => p.status === 'expired').length > 0 && (
              <div className="bg-gray-800 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-orange-300 uppercase mb-3">Ausstehende Picks (offen)</h3>
                <div className="space-y-2">
                  {openPicks.filter(p => p.status === 'expired').map(p => (
                    <div key={p.id} className="bg-gray-700 rounded-lg px-3 py-2 flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-sm">{p.team?.name}</div>
                        <div className="text-xs text-gray-400">R{p.round} · Pick {p.pickNumber} · {p.wasSkipped ? '⏭️ übersprungen' : '🟠 abgelaufen'}</div>
                      </div>
                      <button
                        onClick={() => setSelectedPickId(p.id)}
                        className={`text-xs px-3 py-1 rounded-lg ${selectedPickId === p.id ? 'bg-blue-600' : 'bg-gray-600 hover:bg-gray-50 dark:bg-gray-8000'}`}
                      >
                        {selectedPickId === p.id ? 'Ausgewählt' : 'Für Team picken'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Next waiting picks */}
            <div className="bg-gray-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3">Nächste Picks</h3>
              <div className="space-y-1">
                {picks.filter(p => p.status === 'waiting').slice(0, 8).map(p => (
                  <div key={p.id} className="flex items-center gap-3 py-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-12">#{p.overallPick}</span>
                    <span className="text-sm text-gray-300">{p.team?.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">R{p.round}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Admin pick controls */}
            {isAdmin && (
            <div className="bg-gray-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-purple-300 uppercase mb-3">Admin: Pick ausführen</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Pick-Slot auswählen</label>
                  <select
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-2 text-sm text-white"
                    value={selectedPickId || ''}
                    onChange={e => setSelectedPickId(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">-- Slot wählen --</option>
                    {openPicks.map(p => (
                      <option key={p.id} value={p.id}>
                        #{p.overallPick} {p.team?.name} (R{p.round}) [{p.status === 'expired' && p.wasSkipped ? 'übersprungen' : p.status}]
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Spieler auswählen</label>
                  <select
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-2 text-sm text-white"
                    value={selectedPlayerId}
                    onChange={e => setSelectedPlayerId(e.target.value)}
                  >
                    <option value="">-- Spieler wählen --</option>
                    {availablePlayers.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName} ({p.position}) OVR {p.overallRating}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleMakePick}
                  disabled={saving || !selectedPickId || !selectedPlayerId}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold"
                >
                  {saving ? 'Picke…' : 'Pick bestätigen'}
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Right: available players */}
          <div className="xl:col-span-2">
            <div className="bg-gray-800 rounded-xl p-5">
              <h3 className="text-lg font-bold mb-4">Verfügbare Spieler ({availablePlayers.length})</h3>

              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Name suchen…"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white"
                  value={playerSearch}
                  onChange={e => setPlayerSearch(e.target.value)}
                />
                <select
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white"
                  value={positionFilter}
                  onChange={e => setPositionFilter(e.target.value)}
                >
                  <option value="">Alle Positionen</option>
                  {['PG', 'SG', 'SF', 'PF', 'C'].map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>

              <div className="overflow-y-auto max-h-[60vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="text-gray-400 border-b border-gray-600">
                      <th className="text-left pb-2 pr-3">Spieler</th>
                      <th className="text-left pb-2 pr-3">Pos</th>
                      <th className="text-left pb-2">OVR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availablePlayers.map(p => (
                      <tr
                        key={p.id}
                        className={`border-b border-gray-700 cursor-pointer hover:bg-gray-700 ${selectedPlayerId === String(p.id) ? 'bg-blue-900' : ''}`}
                        onClick={() => setSelectedPlayerId(String(p.id))}
                      >
                        <td className="py-2 pr-3">{p.firstName} {p.lastName}</td>
                        <td className="py-2 pr-3 text-gray-400">{p.position}</td>
                        <td className="py-2">
                          <span className="bg-blue-900 text-blue-200 text-xs px-2 py-0.5 rounded-full">{p.overallRating}</span>
                        </td>
                      </tr>
                    ))}
                    {availablePlayers.length === 0 && (
                      <tr><td colSpan={3} className="py-6 text-center text-gray-500 dark:text-gray-400">Keine Spieler gefunden</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── RESULTS VIEW ─── */}
      {(view === 'results' || fa.status === 'completed') && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-6">Picks &amp; Ergebnisse</h2>
          {Array.from({ length: fa.totalRounds }, (_, i) => i + 1).map(round => (
            <div key={round} className="mb-8">
              <h3 className="text-lg font-semibold text-yellow-300 mb-3">Runde {round}</h3>
              <div className="space-y-2">
                {picks.filter(p => p.round === round).map(p => (
                  <div key={p.id} className={`flex items-center gap-4 rounded-lg px-4 py-3 ${
                    p.status === 'completed' ? 'bg-gray-700' :
                    p.status === 'active' ? 'bg-green-900 border border-green-600' :
                    p.status === 'expired' ? (p.wasSkipped ? 'bg-indigo-900 border border-indigo-600' : 'bg-orange-900 border border-orange-600') : 'bg-gray-750'
                  }`}>
                    <span className="text-gray-400 w-8 text-sm">#{p.overallPick}</span>
                    <div className="w-40">
                      <div className="font-semibold text-sm">{p.team?.name}</div>
                    </div>
                    <div className="flex-1">
                      {p.player ? (
                        <span className="text-white">
                          {p.player.firstName} {p.player.lastName}
                          <span className="text-gray-400 text-xs ml-2">{p.player.position} · OVR {p.player.overallRating}</span>
                        </span>
                      ) : (
                        <span className={`text-xs ${
                          p.status === 'active' ? 'text-green-300' :
                          p.status === 'expired' ? (p.wasSkipped ? 'text-indigo-300' : 'text-orange-300') : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {p.status === 'active' ? '🟢 Am Zug…' :
                           p.status === 'expired' ? (p.wasSkipped ? '⏭️ Übersprungen – Pick ausstehend' : '🟠 Zeit abgelaufen – Pick ausstehend') :
                           '⏳ Wartet…'}
                        </span>
                      )}
                    </div>
                    {p.pickedAt && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(p.pickedAt)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FreeAgencyManagement;
